import { ApolloError, type ApolloQueryResult } from '@apollo/client/core'
import { ResponseError } from './errors'
import { clearCommunicationsError } from './app'
import apollo from '@/apollo'
import INSTANCE_QUERY from '@/graphql/instanceQuery.gql'
import INSTANCE_POLL_RESPONSE from '@/graphql/instancePollResponse.gql'
import INSTANCE_REQUEST_ACTIVE from '@/graphql/instanceRequestActive.gql'
import INSTANCE_CANCEL_REQUEST from '@/graphql/instanceCancelRequest.gql'
import INSTANCE_FORMAT_QUERY from '@/graphql/instanceFormatQuery.gql'
import * as functions from '@/util/functions'
import type { TRowSet, TColumn } from '@/common/query'

const DEFAULT_REQUEST_TIMEOUT = (1 * 60 * 1000) // 1 minute

type TRowSetResponse = {
  rowSet: TRowSet
}

type TRequestContext = {
  instance: string
  pinned: boolean
  fetchOptions: any
  abortController: AbortController
  timeoutId: NodeJS.Timeout
  sql: string
  resolve?: (value: ApolloQueryResult<TRowSetResponse>) => void
  reject?: Function
  navigationCancelled?: boolean
}

const _optionOverrides: any[] = []
const _activeRequests = new Set<TRequestContext>()

export class InstanceQueryService {

  async sql(instanceId: string, database: string, sql: string, options: any = {}): Promise<TRowSet> {
    if (typeof instanceId !== 'string' || !instanceId) {
      throw new TypeError(`Invalid instanceId parameter: [${instanceId}]`)
    }
    if (!!database && typeof database !== 'string') {
      throw new TypeError(`Invalid database parameter: [${database}]`)
    }
    if (!sql || typeof sql !== 'string') {
      throw new TypeError(`Invalid sql parameter: [${sql}]`)
    }

    // Deal with option overrides.
    const optionOverrides = _optionOverrides.at(-1)
    if (optionOverrides) {
      options = { ...options, ...optionOverrides }
    }

    // Gather options
    const { asyncTimeout, initiatedCallback, pollCallback, applicationName = 'ym', pin, timeout = DEFAULT_REQUEST_TIMEOUT, batchKey, parameters, convertToColumnar } = options
    let result

    try {

      // Do we want async timeout handling?
      if (typeof asyncTimeout === 'number') {
        let query = {
          query: INSTANCE_QUERY,
          variables: {
            instanceId,
            databaseName: database || 'yellowbrick',
            query: sql,
            applicationName,
            limit: 0,
            options: {
              parameters,
              convertToColumnar,
              asyncTimeout: typeof pollCallback === 'function' ? 1000 : asyncTimeout // quick poll to let cb have request id if desired
            },
            batchKey
          } as any
        }

        // Does caller want signal of start?
        if (typeof initiatedCallback === 'function') {
          initiatedCallback()
        }

        // We long poll the response to this query here until it completes.
        while (true) {
          const { data: { rowSet } } = await this.abortableQuery(instanceId, query, sql, timeout, pin)
          result = rowSet
          if (result.requestId) { // Did we get a async request?
            // Did we have a poll callback?
            if (typeof pollCallback === 'function') {
              pollCallback(result.requestId)
            }

            query = {
              query: INSTANCE_POLL_RESPONSE,
              variables: {
                instanceId,
                databaseName: database || 'yellowbrick',
                requestId: result.requestId,
                asyncTimeout,
                batchKey
              } as any
            }
          } else {
            break
          }
        }
      } else {
        const query = {
          query: INSTANCE_QUERY,
          variables: {
            instanceId,
            databaseName: database || 'yellowbrick',
            query: sql,
            applicationName,
            limit: 0,
            options: {
              parameters,
              convertToColumnar
            },
            batchKey
          } as any
        }
        const response = await this.abortableQuery(instanceId, query, sql, timeout, pin)
        const { data: { rowSet } } = response
        result = rowSet
      }

      // For this type of invocation, any error gets thrown.
      if (result && result.error) {
        throw new ResponseError({
          error: result.error,
          errorCode: result.errorCode,
          errorFile: result.errorFile,
          errorLine: result.errorLine,
          errorPosition: result.errorPosition,
          errorHint: result.errorHint,
          errorSeverity: result.errorSeverity,
          errorDetail: result.errorDetail,
          context: {
            instance: instanceId
          },
          request: {
            instanceId,
            database,
            applicationName,
            sql,
            parameters
          }
        }, result.error)
      } else {
        clearCommunicationsError()
      }

      return this.convertRowSet(result, options)
    } catch (e: any) {
      if (e instanceof ApolloError) {
        const ae = e as ApolloError

        // Rethrow as a response error if we have a network error.
        if (!!ae.networkError && ae.networkError instanceof Error) {
          const inner = ae.networkError as Error
          const responseError = new ResponseError({
            error: inner.message,
            networkError: inner,
            context: {
              instance: instanceId
            },
            request: {
              instanceId,
              database,
              applicationName,
              sql,
              parameters
            }
          }, inner.message)
          try {
            (responseError as any).cause ??= inner

            const outerStack = responseError.stack ?? `ResponseError: ${responseError.message}`
            const innerStack = inner.stack ?? String(inner)
            responseError.stack = `${outerStack}\nCaused by:\n${innerStack}`
          } catch {
          }
          throw responseError
        }
      }
      throw e
    }
  }

  convertRowSet(rowSet: TRowSet, options: Record<string, any> = {}): TRowSet {
    // Gate on incomplete rowsets (errors, etc)
    if (!rowSet || !rowSet.columns) {
      return rowSet
    }

    // Convert data in row form.
    const convertingColumns: Record<string, TColumn> = functions.indexBy(rowSet.columns, 'name')
    Object.entries(convertingColumns).forEach(([key, value]) => {
      if (!value.type.match(/int8|numeric|decimal|bigint/)) {
        delete convertingColumns[key]
      }
    })
    rowSet.rows?.forEach((row) => {
      Object.values(convertingColumns).forEach((column) => {
        const value = row[column.name]
        if (value !== null && value !== undefined) {
          if (column.type === 'int8' || column.type === 'bigint') {
            row[column.name] = options?.returnBigInt ? BigInt(value) : Number(value)
          } else if (column.type === 'numeric' || column.type === 'decimal') {
            row[column.name] = options?.returnBigNumber ? value : Number(value) // TODO: decimal.js if we need it
          }
        }
      })
    })

    // Convert columnar form.
    rowSet.columns?.forEach((column) => {
      if (column.type === 'int8' || column.type === 'bigint') {
        for (let index = 0; index < column.data.length; ++index) {
          const value = column.data[index]
          if (value !== null && value !== undefined) {
            column.data[index] = options?.returnBigInt ? BigInt(value) : Number(value)
          }
        }
      } else if (column.type === 'numeric' || column.type === 'decimal') {
        for (let index = 0; index < column.data.length; ++index) {
          const value = column.data[index]
          if (value !== null && value !== undefined) {
            column.data[index] = options?.returnBigNumber ? value : Number(value) // TODO: decimal.js if we need it
          }
        }
      }
    })

    // Convert nested rowsets
    rowSet.rowSets?.forEach(rowSet => this.convertRowSet(rowSet))

    return rowSet
  }

  abortableQuery(instanceId: string, query: any, sql: string, timeout: number, pinned: boolean): Promise<ApolloQueryResult<TRowSetResponse>> {

    // Setup the abort controller to handle timing out requests.
    const abortController = new AbortController()
    const { signal } = abortController
    const timeoutId = setTimeout(() => {

      // If the page is no longer visible, just indicate an error that user navigated away
      const hidden = document.visibilityState !== 'visible'
      if (hidden) {
        false && console.error(`Query request timeout with page no longer visible after ${timeout}ms.  Query`, sql)
        const error = new ResponseError(requestContext, 'Navigation abort')
        error.navigationCancelled = true
        abortController.abort(error)

      // This exception is similar to the one thrown by fetch() on a timeout
      } else {
        false && console.error(`Query request timeout after ${timeout}ms.  Query`, sql)
        abortController.abort(new DOMException(`Request timeout`, 'AbortError'))
      }
    }, timeout)

    // Create the request context and set it to the apollo context
    const requestContext: TRequestContext = {
      instance: instanceId,
      pinned,
      fetchOptions: {
        signal
      },
      abortController,
      timeoutId,
      sql
    }
    query.context = requestContext

    // Chain the apollo query so that we can handle rejection on navigation.
    const request = new Promise<ApolloQueryResult<TRowSetResponse>>((resolve, reject) => {
      requestContext.resolve = resolve
      requestContext.reject = reject
      apollo.query(query)
        .then(resolve)
        .catch(reject)
    }).finally(() => {
      _activeRequests.delete(requestContext)
      clearTimeout(timeoutId)
    })
    _activeRequests.add(requestContext)
    return request
  }

  async cancelRequest(instanceId: string, databaseName: string, requestId: string) {
    const response = await apollo.query({
      query: INSTANCE_CANCEL_REQUEST,
      variables: {
        instanceId,
        databaseName,
        requestId
      }
    })
    const {
      data
    } = response
    return data
  }

  async requestActive(instanceId: string, databaseName: string, requestId: string) {
    const response = await apollo.query({
      query: INSTANCE_REQUEST_ACTIVE,
      variables: {
        instanceId,
        databaseName,
        requestId
      }
    })
    const {
      data
    } = response
    return data
  }

  async formatQuery(query: string, parameters: any = {}) {
    const response = await apollo.query({
      query: INSTANCE_FORMAT_QUERY,
      variables: {
        query,
        parameters
      }
    })
    const {
      data: { formatQuery }
    } = response
    return formatQuery
  }

  pushOptionOverrides(options: any) {
    _optionOverrides.push(options)
  }

  popOptionOverrides() {
    _optionOverrides.pop()
  }

  abortActiveRequests(instance: string, instanceFrom: string) {
    if (instance === instanceFrom) {
      return
    }
    // NB: only abort requests in the instance views.
    Array.from(_activeRequests)
      .filter(r => !!r.instance) // Request has an instance
      .filter(r => r.instance !== instance) // And its not this instance
      .filter(r => !r.pinned) // And its not pinned
      .forEach((requestContext) => {
        const { timeoutId, reject, abortController } = requestContext

        // Indicate that navigation cancelled this query.
        requestContext.navigationCancelled = true

        // Clear the timeout
        window.clearTimeout(timeoutId)

        // Make a response error indicating why we cancelled and reject the request.
        const reason = 'Navigation abort'
        const error = new ResponseError(requestContext, reason)
        error.navigationCancelled = true
        reject && reject(error)

        // Stop the http transaction
        abortController.abort(new DOMException(reason, 'AbortError'))

        // Remove the request
        _activeRequests.delete(requestContext)
      })
  }
}

export default new InstanceQueryService()
